import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import DashboardSidebar from "../components/DashboardSidebar";
import DashboardHeader from "../components/DashboardHeader";
import {
  Search,
  ArrowRight,
  BookOpen,
  ShieldCheck,
  Scale,
  Users,
  Heart,
  UserCheck,
  Stethoscope,
  AlertTriangle,
  FileText,
  Baby,
  Lock,
  ClipboardCheck,
  BadgeAlert,
  UsersRound,
  HeartHandshake,
  HandHeart,
  BookMarked,
  Loader2,
  MessagesSquare,
  Newspaper,
  Landmark,
  Mic,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------- helpers ---------- */
const pickIcon = (title = "") => {
  const t = title.toLowerCase();
  if (t.includes("confidential")) return Lock;
  if (t.includes("capacity")) return ClipboardCheck;
  if (t.includes("consent")) return UserCheck;
  if (t.includes("gillick") || t.includes("fraser") || t.includes("child")) return Baby;
  if (t.includes("candour")) return HeartHandshake;
  if (t.includes("safeguard")) return ShieldCheck;
  if (t.includes("whistle") || t.includes("raising concerns")) return BadgeAlert;
  if (t.includes("professional")) return Stethoscope;
  if (t.includes("equality") || t.includes("diversity") || t.includes("inclusion")) return UsersRound;
  if (t.includes("advance") || t.includes("adrt")) return FileText;
  if (t.includes("power of attorney") || t.includes("lpa")) return Scale;
  if (t.includes("dnacpr")) return AlertTriangle;
  if (t.includes("organ")) return HandHeart;
  if (t.includes("assisted") || t.includes("euthan")) return Heart;
  if (t.includes("abortion")) return Users;
  if (t.includes("resource allocation") || t.includes("qaly")) return Scale;
  if (t.includes("pillars")) return BookMarked;
  if (t.includes("station") || t.includes("mmi")) return Mic;
  if (t.includes("news") || t.includes("outbreak") || t.includes("crisis")) return Newspaper;
  if (t.includes("nhs")) return Landmark;
  return BookOpen;
};

/* Small brand mark per guide (used inside the tab pill) */
const guideAccent = (slug) => {
  switch (slug) {
    case "medical-ethics-guide":
      return { icon: ShieldCheck };
    case "medical-case-studies-guide":
      return { icon: FileText };
    case "mmi-station-techniques-guide":
      return { icon: Mic };
    case "nhs-constitution-guide":
      return { icon: Landmark };
    case "nhs-hot-topics-guide":
      return { icon: Newspaper };
    default:
      return { icon: BookOpen };
  }
};

/* Stage 4: single consistent icon per guide for three specific guides. */
const GUIDE_FORCED_ICON = {
  "medical-ethics-guide": ShieldCheck,
  "nhs-constitution-guide": Landmark,
  "nhs-hot-topics-guide": Newspaper,
};

const ResourceCard = ({ topic, guideSlug, guideTitle, showGuide = false }) => {
  const navigate = useNavigate();
  const Icon = GUIDE_FORCED_ICON[guideSlug] || pickIcon(topic.title);
  const completed =
    typeof window !== "undefined" &&
    localStorage.getItem(`medily.progress.${guideSlug}.${topic.slug}`) === "1";
  return (
    <div
      onClick={() => navigate(`/resources/${guideSlug}/${topic.slug}`)}
      onKeyDown={(e) => (e.key === "Enter" ? navigate(`/resources/${guideSlug}/${topic.slug}`) : null)}
      role="button"
      tabIndex={0}
      className="rounded-2xl bg-white border border-slate-100 overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition cursor-pointer group flex flex-col"
    >
      <div className="h-32 bg-gradient-to-br from-[#dbe6ff] via-[#e9efff] to-[#f5f8ff] relative overflow-hidden">
        <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/40 blur-2xl" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 rounded-2xl bg-white/80 backdrop-blur border border-white shadow-sm flex items-center justify-center text-[#1d4ed8] group-hover:scale-110 group-hover:rotate-3 transition">
            <Icon className="w-7 h-7" />
          </div>
        </div>
        <div className="absolute top-3 left-3 text-[10px] font-medium tracking-wide px-2 py-0.5 rounded-full bg-white/80 text-[#1d4ed8]">
          {topic.read_minutes} min read
        </div>
        {completed && (
          <div className="absolute top-3 right-3 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#15803d] text-white inline-flex items-center gap-1">
            ✓ Completed
          </div>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        {showGuide && (
          <p className="text-[10.5px] font-semibold uppercase tracking-wide text-[#1d4ed8] mb-1">
            {guideTitle}
          </p>
        )}
        <p className="font-semibold text-slate-900 text-[15px] group-hover:text-[#1d4ed8] leading-tight">
          {topic.title}
        </p>
        <p className="text-[12.5px] text-slate-500 mt-1.5 line-clamp-2 leading-relaxed flex-1">
          {topic.description || "Explore this topic."}
        </p>
        <span className="mt-3 text-[13px] font-medium text-[#1d4ed8] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
          Continue Reading <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </div>
  );
};

const Resources = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialGuideParam = new URLSearchParams(location.search).get("guide");
  const [guides, setGuides] = useState([]);
  const [activeGuide, setActiveGuide] = useState(initialGuideParam);
  const [guideCache, setGuideCache] = useState({}); // slug -> data (topics)
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [error, setError] = useState("");

  // fetch guides list once
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/guides`);
        if (!mounted) return;
        const list = res.data.guides || [];
        setGuides(list);
        setActiveGuide((prev) => {
          if (prev && list.some((g) => g.slug === prev)) return prev;
          return list.length ? list[0].slug : null;
        });
        if (!list.length) setLoading(false);
      } catch {
        setError("Could not load guides.");
        setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Sync activeGuide with URL ?guide= param when it changes
  useEffect(() => {
    const p = new URLSearchParams(location.search).get("guide");
    if (p && guides.some((g) => g.slug === p) && p !== activeGuide) {
      setActiveGuide(p);
      setQ("");
    }
  }, [location.search, guides, activeGuide]);

  // fetch active guide detail on demand
  useEffect(() => {
    if (!activeGuide) return;
    if (guideCache[activeGuide]) {
      setLoading(false);
      return;
    }
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get(`${API}/guides/${activeGuide}`);
        if (!mounted) return;
        setGuideCache((c) => ({ ...c, [activeGuide]: res.data }));
      } catch {
        setError("Could not load guide.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [activeGuide, guideCache]);

  // If the user is searching, pre-fetch every guide so results span all 5.
  const searching = q.trim().length > 0;
  useEffect(() => {
    if (!searching || !guides.length) return;
    let mounted = true;
    (async () => {
      const missing = guides.filter((g) => !guideCache[g.slug]);
      if (!missing.length) return;
      const results = await Promise.all(
        missing.map((g) => axios.get(`${API}/guides/${g.slug}`).then((r) => [g.slug, r.data]).catch(() => null))
      );
      if (!mounted) return;
      const updates = {};
      results.forEach((r) => {
        if (r) updates[r[0]] = r[1];
      });
      if (Object.keys(updates).length) setGuideCache((c) => ({ ...c, ...updates }));
    })();
    return () => {
      mounted = false;
    };
  }, [searching, guides, guideCache]);

  const activeGuideData = guideCache[activeGuide];

  const activeTopics = useMemo(
    () => (activeGuideData?.topics || []),
    [activeGuideData]
  );

  const searchResults = useMemo(() => {
    if (!searching) return null;
    const needle = q.toLowerCase();
    const out = [];
    guides.forEach((g) => {
      const data = guideCache[g.slug];
      if (!data) return;
      data.topics.forEach((t) => {
        if (
          t.title.toLowerCase().includes(needle) ||
          (t.description || "").toLowerCase().includes(needle)
        ) {
          out.push({ topic: t, guide: g });
        }
      });
    });
    return out;
  }, [searching, q, guides, guideCache]);

  return (
    <div className="min-h-screen bg-[#f5f8fd] flex">
      <DashboardSidebar />
      <div className="flex-1 min-w-0">
        <DashboardHeader />
        <main className="p-6 lg:p-10">
          <section className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6 md:p-10 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-[420px] h-[420px] bg-[#dbe6ff] rounded-full blur-3xl opacity-60 -z-0" />

            <div className="relative flex items-start justify-between flex-wrap gap-6">
              <div>
                <span className="inline-block px-3 py-1 rounded-full bg-[#eef4ff] text-[#1d4ed8] text-[11px] font-semibold tracking-wide uppercase">
                  Study Resources
                </span>
                <h1 className="mt-3 text-[30px] md:text-[36px] font-semibold text-slate-900 tracking-tight leading-tight">
                  Your Learning Hub
                </h1>
                <p className="mt-2 text-slate-500 max-w-2xl text-[14.5px]">
                  Browse expert-written guides, Key Concepts and Common MMI Pitfalls — organised by topic and tailored for your MMI.
                </p>
              </div>
              <div className="relative w-full max-w-sm">
                <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search all guides..."
                  className="w-full pl-10 pr-4 py-3 rounded-full border border-slate-200 text-sm bg-white/90 focus:outline-none focus:border-[#1d4ed8] focus:ring-2 focus:ring-[#1d4ed8]/10"
                />
              </div>
            </div>

            {/* Guide tabs */}
            <div className="relative mt-6 flex flex-wrap gap-2">
              {guides.map((g) => {
                const Accent = guideAccent(g.slug).icon;
                const isActive = activeGuide === g.slug && !searching;
                return (
                  <button
                    key={g.slug}
                    onClick={() => {
                      setActiveGuide(g.slug);
                      setQ("");
                      navigate(`/resources?guide=${g.slug}`, { replace: true });
                    }}
                    className={`px-5 py-2 rounded-full text-[13px] font-medium border transition inline-flex items-center gap-2 ${
                      isActive
                        ? "bg-[#1d4ed8] text-white border-[#1d4ed8] shadow-sm"
                        : "bg-white text-slate-700 border-slate-200 hover:border-[#1d4ed8] hover:text-[#1d4ed8]"
                    }`}
                  >
                    <Accent className="w-3.5 h-3.5" />
                    {g.title}
                    <span
                      className={`text-[11px] px-1.5 py-0.5 rounded-full ${
                        isActive ? "bg-white/20" : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {g.topic_count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Content */}
            <div className="relative mt-8">
              {loading && (
                <div className="flex items-center justify-center py-16 text-slate-400">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                </div>
              )}
              {error && !loading && (
                <div className="text-center text-red-500 py-10">{error}</div>
              )}

              {!loading && !error && searching && (
                <>
                  <div className="mb-4 text-[13.5px] text-slate-500">
                    {searchResults === null ? (
                      "Searching…"
                    ) : (
                      <>
                        <span className="font-medium text-slate-700">{searchResults.length}</span> result
                        {searchResults.length === 1 ? "" : "s"} across{" "}
                        <span className="font-medium text-slate-700">{guides.length}</span> guides
                      </>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                    {(searchResults || []).map((r) => (
                      <ResourceCard
                        key={`${r.guide.slug}/${r.topic.slug}`}
                        topic={r.topic}
                        guideSlug={r.guide.slug}
                        guideTitle={r.guide.title}
                        showGuide
                      />
                    ))}
                    {searchResults && searchResults.length === 0 && (
                      <div className="col-span-full text-center text-slate-400 py-10">
                        No topics match your search.
                      </div>
                    )}
                  </div>
                </>
              )}

              {!loading && !error && !searching && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                  {activeTopics.map((t) => (
                    <ResourceCard
                      key={t.slug}
                      topic={t}
                      guideSlug={activeGuide}
                      guideTitle={activeGuideData?.title}
                    />
                  ))}
                  {activeTopics.length === 0 && (
                    <div className="col-span-full text-center text-slate-400 py-10">
                      No topics.
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default Resources;
